import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { config } from '../config';
import { getApiHeaders } from '../api/apiHeaders';

const url_api = config.api.web;

/**
 * Real OHLCV candles for a symbol/timeframe (GET stock/candles).
 * Results are cached per symbol+timeframe so switching tabs back is instant.
 */
export default function useStockCandles(symbol, timeframe, userId, enabled = true) {
    const [candles, setCandles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const cacheRef = useRef({});

    useEffect(() => {
        const sym = String(symbol || '').trim().toUpperCase();
        if (!enabled || !sym || !userId) {
            return undefined;
        }

        const cacheKey = `${sym}:${timeframe}`;
        const cached = cacheRef.current[cacheKey];
        if (cached && cached.length) {
            setCandles(cached);
            setError('');
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError('');
        setCandles([]);

        axios
            .get(`${url_api}stock/candles`, {
                params: { id: userId, symbol: sym, timeframe },
                headers: getApiHeaders(),
                withCredentials: true,
            })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                if (response.status < 300 && response.data?.status === 'Success') {
                    const list = Array.isArray(response.data.body?.list)
                        ? response.data.body.list
                        : [];
                    if (list.length) {
                        cacheRef.current[cacheKey] = list;
                        setCandles(list);
                        setError('');
                    } else {
                        setCandles([]);
                        setError('try_later');
                    }
                } else {
                    setCandles([]);
                    setError('try_later');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCandles([]);
                    setError('try_later');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [symbol, timeframe, userId, enabled]);

    return { candles, loading, error };
}
